<template>
  <section class="d-flex">
    <div class="testimonial-section d-flex flex-row gap-8">
      <div class="d-flex flex-column flex-grow-1 overflow-hidden testimonial-content">
        <UeInlineEditable
          class="heading"
          name="text"
          :editable="editable"
          v-model="localSection.heading"
          placeholder="Testimonial"
          @editable_field:save="updateHeading"
        />

        <UeInlineEditable
          name="text"
          :editable="editable"
          v-model="localSection.text"
          class="text"
          placeholder="Write the testimonial here"
          @editable_field:save="updateText"
        />

        <div class="d-flex flex-row align-items-center bottom">
          <div class="d-flex flex-column justify-content-center align-items-center">
            <div
              class="headshot"
              v-if="localSection.image_blob_url"
              @click.stop="focused = !focused"
              v-click-outside="closeControls"
            >
              <div
                v-if="focused && editable"
                class="controls d-flex align-items-center justify-content-center"
                @click.stop="deleteImage"
              >
                <b-icon-trash />
              </div>
              <img class="testimonial-image" :src="localSection.image_blob_url" />
            </div>
            <div v-else>
              <UeDropzone
                v-if="editable"
                id="testimonial-image-dropzone"
                ref="testimonialImageDropzone"
                class="d-flex align-items-center justify-content-center"
                :useCustomSlot="true"
                :includeStyling="false"
                :options="dropzoneOptions"
                @vdropzone-success="uploadSuccess"
              >
                <div class="testimonial-image">
                  <i class="bi bi-image"></i>
                </div>
              </UeDropzone>
            </div>
          </div>
          <div class="d-flex flex-column overflow-hidden testimonial-attribution">
            <UeInlineEditable
              name="attribution_name"
              :editable="editable"
              v-model="localSection.attribution_name"
              class="attribution_name"
              placeholder="Write the name here"
              @editable_field:save="updateName"
            />
            <UeInlineEditable
              name="attribution_role"
              :editable="editable"
              v-model="localSection.attribution_role"
              class="attribution_role"
              placeholder="Write the role here"
              @editable_field:save="updateRole"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="d-flex align-items-center ml-1">
      <SectionControls
        :recipient-id="recipientId"
        :case-study-id="caseStudyId"
        :index="index"
        v-if="editable"
      />
    </div>
  </section>
</template>
<script>
import SectionMixin from '@app/mixins/case_study_section'
import SectionControls from '@components/case_study_sections/SectionControls.vue'
import { getCsrfToken } from '@lib/csrf'
import { useCaseStudyStore } from '@app/stores/CaseStudyStore'
import { removeCaseStudyTestimonialImage } from '@app/api'
import clickOutside from '@/lib/directives/clickOutside'

export default {
  name: 'TestimonialSection',
  components: { SectionControls },
  mixins: [SectionMixin],
  directives: { clickOutside },
  props: {
    recipientId: { type: Number, required: true },
    caseStudyId: { type: Number, required: true },
    section: { type: Object, required: true },
    index: { type: Number, required: true },
    accentColor: { type: String, required: true },
    editable: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      focused: false,
      localSection: { ...this.section },
    }
  },
  watch: {
    section: {
      handler(newSection) {
        this.localSection = { ...newSection }
      },
      deep: true,
    },
  },
  computed: {
    editableText() {
      // In Vue 3 you have to use null/undefined to make an attribute conditionally
      // not render, false won't work
      return this.editable ? true : null
    },
    dropzoneOptions() {
      return {
        url: `/app/api/v2/case_studies/${this.caseStudyId}/sections/testimonial_section_set_image.json?response_id=${this.localSection.response_id}`,
        headers: { 'X-CSRF-Token': getCsrfToken() },
        acceptedFiles: 'image/*',
        disablePreviews: true,
        createImageThumbnails: false,
      }
    },
  },
  methods: {
    move(forward) {
      this.$emit('move-section', { index: this.index, forward })
    },
    updateHeading(newContent) {
      this.updateByField('heading')(newContent)
    },
    updateText(newContent) {
      this.updateByField('text')(newContent)
    },
    updateName(newContent) {
      this.updateByField('attribution_name')(newContent)
    },
    updateRole(newContent) {
      this.updateByField('attribution_role')(newContent)
    },
    uploadSuccess(_file, response) {
      useCaseStudyStore().updateCaseStudy(response.case_study)
      this.focused = false
    },
    deleteImage() {
      removeCaseStudyTestimonialImage(this.caseStudyId, this.localSection.response_id).then(
        (response) => {
          useCaseStudyStore().updateCaseStudy(response.data.case_study)
        },
      )
    },
    closeControls() {
      this.focused = false
    },
  },
}
</script>

<style lang="sass" scoped>
.delete
  cursor: pointer

.testimonial-section
  padding: 24px 32px
  border: 1px solid #E9ECEF
  border-radius: 14px
  overflow: hidden
  width: 100%
  display: flex
  gap: 24px

.testimonial-attribution
  padding: 4px

.testimonial-content
  padding: 4px

.heading
  font-size: 16px
  margin-bottom: 32px
  color: #6C747D
  overflow-wrap: anywhere

.text
  font-size: 16px
  margin-bottom: 32px
  color: black
  font-weight: 600
  overflow-wrap: anywhere

.attribution_name
  font-size: 16px
  font-weight: 600
  color: #6C747D
  overflow-wrap: anywhere
  margin-bottom: 4px

.attribution_role
  font-size: 16px
  font-weight: 400
  color: #6C747D
  overflow-wrap: anywhere

.testimonial-image
  width: 100px
  height: 100px
  object-fit: cover
  border-radius: 50%
  background-color: #E9ECEF
  display: flex
  flex-direction: column
  justify-content: center
  align-items: center
  font-size: 12px
  text-align: center
  color: #6C747D
  font-weight: 600
  margin-right: 24px
  i
    font-size: 16px
  img
    border-radius: 14px

.headshot
  position: relative

.controls
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1)
  position: absolute
  background: white
  border-radius: 10px
  height: 45px
  width: 45px
  z-index: 10
  margin-top: calc(50% - 22.5px)
  right: calc(50% - 22.5px)
  border: 1px solid var(--border-color)
  cursor: pointer

#testimonial-image-dropzone
  cursor: pointer

  .dz-success-mark .dz-error-mark
    display: none
  .dz-success-mark, .dz-error-mark
    display: none
  ::v-deep .dz-preview
    display: none
</style>
